/**
 * Porte « code à usage unique » des pages apprenant ouvertes par lien personnel (formulaire, positionnement).
 * Le serveur répond 403 `code_requis` tant que le navigateur n'a pas de preuve d'accès : on propose alors d'envoyer un
 * code à six chiffres à l'adresse de l'apprenant (affichée masquée), puis de le saisir.
 *
 * Recette du 02/11/2026 (bug bloquant) : l'apprenant quitte la page pour lire sa messagerie ; au retour, l'écran de
 * saisie avait disparu (le retour sur l'onglet relançait la requête de la page, qui démontait cette porte et son état
 * « code envoyé » ; sur téléphone, l'onglet déchargé se rechargeait). Désormais l'étape de saisie est reconstruite à
 * partir du défi en cours renvoyé par le serveur (et d'une copie locale datée) : après un changement d'application,
 * une mise en veille, un rechargement ou dans un nouvel onglet, on retombe sur le champ, adresse et temps restant
 * rappelés. Le lien de l'e-mail (`#code=…`) pré-remplit le code.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Clock, KeyRound, Mail } from "lucide-react";
import { api, ErreurApi } from "../api";
import { Alerte, Bouton, Carte, Champ, useTitreDocument } from "./base";
import { codeDuFragment, defiActif, LONGUEUR_CODE, lireDefi, memoriserDefi, normaliserCode, oublierDefi, resteLisible, type DefiCode, type TypeLienPublic } from "./defi-code";

export const codeRequis = (e: unknown): e is ErreurApi => e instanceof ErreurApi && e.statut === 403 && e.code === "code_requis";

type DetailsAcces = { email_masque?: string; defi?: DefiCode | null };
type EtatAcces = DetailsAcces & { ouvert?: boolean };

/** `localStorage` peut être refusé (navigation privée, réglages) : on retombe alors sur le seul serveur. */
function stockage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const heureFr = (iso: string) => new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

export function PorteCode({ type, jeton, erreur, ouvrir }: { type: TypeLienPublic; jeton: string; erreur: ErreurApi; ouvrir: () => void }) {
  useTitreDocument("Vérification de votre identité");
  const details = (erreur.details ?? null) as DetailsAcces | null;
  const emailMasque = details?.email_masque ?? "votre adresse";
  const base = `/public/${type}/${jeton}/acces`;

  const [defi, setDefi] = useState<DefiCode | null>(() => {
    const st = stockage();
    const local = st ? lireDefi(st, type, jeton) : null;
    const d = defiActif(details?.defi, local);
    if (st) {
      if (d) memoriserDefi(st, type, jeton, d);
      else oublierDefi(st, type, jeton);
    }
    return d;
  });
  // Lien de l'e-mail : code pré-rempli, jamais validé tout seul (un robot d'analyse de liens ne le consomme pas).
  const [codeDuLien] = useState(() => codeDuFragment(window.location.hash));
  const [code, setCode] = useState(codeDuLien ?? "");
  const [maintenant, setMaintenant] = useState(() => Date.now());
  const [info, setInfo] = useState<string | null>(null);
  const idChamp = useId();

  const retenir = (d: DefiCode | null) => {
    setDefi(d);
    const st = stockage();
    if (st) {
      if (d) memoriserDefi(st, type, jeton, d);
      else oublierDefi(st, type, jeton);
    }
  };

  // Le code ne reste pas dans la barre d'adresse (historique, partage d'écran).
  useEffect(() => {
    if (codeDuLien) window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  }, [codeDuLien]);

  // Compte à rebours.
  useEffect(() => {
    const t = window.setInterval(() => setMaintenant(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Retour sur la page (autre onglet, application Mail, mise en veille) : on interroge l'état SANS rien démonter.
  // Si le code a été validé dans un autre onglet du même navigateur, la page s'ouvre ; sinon le défi est rafraîchi.
  const ouvrirRef = useRef(ouvrir);
  ouvrirRef.current = ouvrir;
  useEffect(() => {
    let actif = true;
    const verifierEtat = () => {
      if (document.visibilityState !== "visible") return;
      setMaintenant(Date.now());
      api
        .get<EtatAcces>(base)
        .then((r) => {
          if (!actif) return;
          if (r.ouvert) ouvrirRef.current();
          else if (r.defi !== undefined) setDefi((avant) => {
            const d = defiActif(r.defi, avant);
            const st = stockage();
            if (st) {
              if (d) memoriserDefi(st, type, jeton, d);
              else oublierDefi(st, type, jeton);
            }
            return d;
          });
        })
        .catch(() => undefined); // hors ligne : on garde l'écran tel quel
    };
    document.addEventListener("visibilitychange", verifierEtat);
    window.addEventListener("pageshow", verifierEtat);
    return () => {
      actif = false;
      document.removeEventListener("visibilitychange", verifierEtat);
      window.removeEventListener("pageshow", verifierEtat);
    };
  }, [base, type, jeton]);

  const envoyer = useMutation({
    mutationFn: () => api.post<DefiCode>(`${base}/code`),
    onSuccess: (r) => {
      const renvoi = Boolean(defi);
      retenir({ ...r, email_masque: emailMasque });
      setCode("");
      setInfo(renvoi ? "Nouveau code envoyé. Il remplace le précédent : saisissez le code du dernier e-mail reçu." : null);
      verifier.reset();
      window.setTimeout(() => document.getElementById(idChamp)?.focus(), 0);
    },
    onError: (e) => {
      // Renvoi trop rapproché : le serveur rappelle le défi en cours, on reste sur la saisie.
      const d = (e as ErreurApi).details as DefiCode | null;
      if (d && typeof d === "object" && "expire_le" in d) retenir({ ...d, email_masque: emailMasque });
    },
  });
  const verifier = useMutation({
    mutationFn: (valeur: string) => api.post(`${base}/verifier`, { code: valeur }),
    onSuccess: () => {
      const st = stockage();
      if (st) oublierDefi(st, type, jeton);
      ouvrir();
    },
    onError: () => {
      // Essais restants, échéance : l'écran reflète l'état du serveur.
      api
        .get<EtatAcces>(base)
        .then((r) => {
          if (r.ouvert) ouvrir();
          // Plus aucun code en attente (déjà servi ailleurs, échu) : retour à « Recevoir un code », message conservé.
          else retenir(r.defi ? { ...r.defi, email_masque: emailMasque } : null);
        })
        .catch(() => undefined);
    },
  });

  const saisir = (texte: string) => {
    const propre = normaliserCode(texte);
    setCode(propre);
    // Validation automatique dès que le code est complet (saisie, collage ou suggestion du clavier).
    if (propre.length === LONGUEUR_CODE && propre !== code && !verifier.isPending) verifier.mutate(propre);
  };

  const enCours = defi !== null && Date.parse(defi.expire_le) > maintenant;
  const expire = defi !== null && !enCours;
  const saisie = enCours || (codeDuLien !== null && !expire);
  const bloque = enCours && defi.essais_restants === 0;
  const renvoiDans = defi ? Date.parse(defi.renvoi_possible_le) - maintenant : 0;
  const emailAffiche = defi?.email_masque ?? emailMasque;

  return (
    <div className="pt-8 sm:pt-12">
      <Carte className="mx-auto max-w-md p-5 sm:p-6">
        <div className="mb-4 grid size-12 place-items-center rounded-full bg-papier-3 text-accent"><KeyRound className="size-6" aria-hidden /></div>
        <h1 className="text-[22px] leading-tight font-semibold">Vérification de votre identité</h1>
        {!saisie ? (
          <>
            <p className="mt-2 text-encre-2">
              Pour protéger vos données, nous envoyons un code à usage unique à <strong className="text-encre">{emailAffiche}</strong>. Il est valable 15 minutes.
            </p>
            {verifier.error && <div className="mt-4"><Alerte ton="danger">{verifier.error.message}</Alerte></div>}
            {expire && <div className="mt-4"><Alerte ton="attention">Le code envoyé à {heureFr(defi.envoye_le)} a expiré. Demandez-en un nouveau.</Alerte></div>}
            <Bouton variante="primaire" className="mt-5 w-full" enCours={envoyer.isPending} onClick={() => { verifier.reset(); envoyer.mutate(); }}>
              <Mail className="size-4" aria-hidden /> {expire ? "Recevoir un nouveau code" : "Recevoir mon code"}
            </Bouton>
          </>
        ) : (
          <form className="mt-2 space-y-4" onSubmit={(e) => { e.preventDefault(); if (code.length === LONGUEUR_CODE) verifier.mutate(code); }}>
            <p className="text-encre-2">
              {codeDuLien && !defi ? (
                <>Le code de votre e-mail est pré-rempli : appuyez sur « Ouvrir ma page ».</>
              ) : (
                <>
                  Code envoyé à <strong className="text-encre">{emailAffiche}</strong>
                  {defi && <> à {heureFr(defi.envoye_le)}</>}. Vous pouvez quitter cette page pour consulter votre messagerie : elle vous attendra ici.
                </>
              )}
            </p>
            {defi && enCours && (
              <p className="flex items-center gap-2 text-sm text-encre-2" aria-live="off">
                <Clock className="size-4 shrink-0" aria-hidden /> Valable encore <strong className="text-encre tabular-nums">{resteLisible(Date.parse(defi.expire_le) - maintenant)}</strong>
              </p>
            )}
            {info && <Alerte ton="info">{info}</Alerte>}
            <Champ
              id={idChamp}
              libelle="Code reçu par e-mail"
              aide="Six chiffres. Vous pouvez coller le code tel quel."
              inputMode="numeric"
              autoComplete="one-time-code"
              enterKeyHint="go"
              autoFocus
              required
              disabled={bloque}
              value={code}
              onChange={(e) => saisir(e.target.value)}
              className="h-14! text-center text-2xl font-semibold tracking-[0.4em] tabular-nums"
            />
            {verifier.error && <Alerte ton="danger">{verifier.error.message}</Alerte>}
            <Bouton type="submit" variante="primaire" className="w-full" enCours={verifier.isPending} disabled={bloque || code.length !== LONGUEUR_CODE}>Ouvrir ma page</Bouton>
            <Bouton type="button" variante="secondaire" className="w-full" enCours={envoyer.isPending} disabled={renvoiDans > 0} onClick={() => envoyer.mutate()}>
              <Mail className="size-4" aria-hidden /> {renvoiDans > 0 ? `Renvoyer un code (dans ${resteLisible(renvoiDans)})` : "Je n'ai rien reçu : renvoyer un code"}
            </Bouton>
            <p className="text-xs text-encre-3">Pensez au dossier des indésirables. Si vous demandez un nouveau code, seul le dernier reçu sera valable.</p>
          </form>
        )}
        {envoyer.error && <div className="mt-4"><Alerte ton="danger">{envoyer.error.message}</Alerte></div>}
      </Carte>
    </div>
  );
}
